import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { notifTitle } from "@/lib/notifText";


const POLL_MS = 8000;
let state = { count: 0, latestId: null };
const listeners = new Set();
const setState = (next) => { state = { ...state, ...next }; listeners.forEach((l) => l(state)); };

export function useUnreadCount() {
  const [count, setCount] = useState(state.count);
  useEffect(() => {
    const l = (s) => setCount(s.count);
    listeners.add(l);
    return () => listeners.delete(l);
  }, []);
  return count;
}

export const refreshUnread = () => window.dispatchEvent(new Event("bazaro-notifications"));
export const adjustUnread = (delta) => setState({ count: Math.max(0, state.count + delta) });

const ADMIN_TAB_ALIAS = { businesses: "companies", activity: "logs", wallet: "pricing", transfers: "pricing" };
const BUSINESS_TYPES = new Set(["business_status", "business_decision"]);

// Maps app-style and web-style action URLs to website routes.
export const mapActionUrl = (url = "", type = "") => {
  const [path, query = ""] = String(url || "").split("?");
  if (path.startsWith("/product/") || path.startsWith("/ad/")) return path.replace(/^\/(product|ad)\//, "/listing/");
  if (path.startsWith("/receipts/")) return `/payment-receipts?no=${path.split("/")[2]}`;
  if (path.startsWith("/support/assigned")) return path.replace("/support/assigned", "/assigned-support");
  if (path.startsWith("/chat/")) return path.replace("/chat/", "/messages/");
  if (path === "/chat" || path === "/chats" || path === "/messages") return "/messages";
  if (path.startsWith("/receipts")) return "/payment-receipts";
  if (path === "/payments") return "/payment-history";
  if (path === "/profile") return "/account";
  if (path === "/support") return type === "support_chat" ? "/support/live" : "/customer-service";
  if (path === "/post" && BUSINESS_TYPES.has(type)) return "/my-business";
  if (path === "/business" || path === "/my-business") return "/my-business";
  if (path === "/admin") {
    const p = new URLSearchParams(query);
    const tab = p.get("tab");
    if (tab && ADMIN_TAB_ALIAS[tab]) p.set("tab", ADMIN_TAB_ALIAS[tab]);
    const q = p.toString();
    return q ? `/admin?${q}` : "/admin";
  }
  return url || "/";
};

export function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = ctx.currentTime + i * 0.15;
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.4);
    });
    setTimeout(() => ctx.close(), 1000);
  } catch { /* audio unavailable */ }
}

export function NotificationWatcher() {
  const { user } = useAuth();
  const userId = user?.user_id;
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;
  useEffect(() => {
    if (!userId) { setState({ count: 0, latestId: null }); return undefined; }
    let alive = true;
    let first = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (!alive) return;
        const latest = data.latest;
        const isNew = latest && latest.notif_id !== state.latestId && !latest.read;
        if (isNew && !first) {
          playChime();
          window.dispatchEvent(new Event("bazaro-new-notification"));
          const en = document.documentElement.lang === "en";
          toast((en && latest.title_en) || notifTitle(latest.title, en ? "en" : "ar"), {
            description: (en && latest.message_en) || latest.message,
            action: { label: document.documentElement.lang === "en" ? "View" : "عرض", onClick: () => {
              api.post(`/notifications/${latest.notif_id}/read`).then(refreshUnread).catch(() => {});
              navRef.current(mapActionUrl(latest.action_url, latest.type));
            } },
          });
        }
        first = false;
        setState({ count: data.count, latestId: latest?.notif_id || state.latestId });
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener("bazaro-notifications", load);
    window.addEventListener("focus", onFocus);
    return () => { alive = false; clearInterval(id); window.removeEventListener("bazaro-notifications", load); window.removeEventListener("focus", onFocus); };
  }, [userId]);
  return null;
}
